import { defineComponent, h } from 'vue'

import { event } from 'quasar'

import { BsComponentRef, bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'

import stopAndPrevent = event.stopAndPrevent
import { childComponents } from '../childComponents'
import { domHandlers } from '../quasarHandlers'

/** A `$refs` entry holding a BsForm — see `BsComponentRef`, which this adds the form's own hook to. */
export type BsFormRef = BsComponentRef & {
  validateForm: () => Promise<boolean>
}

/**
 * The form element. It validates by walking its own children rather than holding a field registry, so
 * anything with a `validate()` under it takes part without registering — see `validateComponentTree`.
 *
 * On a valid submit it re-emits `submit` if anything is listening, and otherwise submits the form
 * element itself, which is what makes a plain form still work.
 */
export default defineComponent({
  name: 'BsForm',

  props: { ...bsComponentProps },

  setup (props, { emit }) {
    const vm = useVm()
    // `reset` is left out of the spread: BsForm's own reset walks its children rather than restoring one
    // field's initial value, exactly as the class overrode the mixin's. Returning both would let the
    // method quietly overwrite the composable's binding instead of saying so here.
    const { reset, ...bs } = useBsComponent({ vm, props, emit })
    void reset

    return { ...bs }
  },

  /**
   * Declared so a native `submit` on the <form> is not ALSO counted as a component emission -- one
   * real submit was arriving twice -- and so a parent's `onSubmit` stops falling through as a second
   * native listener, which had Vue merging it with ours into an array of handlers.
   */
  emits: ['input', 'submit'],

  methods: {
    validateForm (): Promise<boolean> {
      const valid = this.validateComponentTree(childComponents(this))
      return Promise.resolve(valid)
    },

    resetComponentTree (components: any) {
      for (const component of components) {
        const children = childComponents(component)
        if (children.length > 0) {
          this.resetComponentTree(children)
        }

        if (component.reset === void 0) {
          continue // No reset function; skip.
        }

        component.reset()
      }
    },

    reset () {
      this.resetComponentTree(childComponents(this))
    }
  },

  render () {
    // Module scope would be shared by every form on the page; this is per render call, which is all
    // the fence needs -- the re-entry happens synchronously inside `evt.target.submit()`.
    let nativeSubmitInFlight = false

    return h('form', {
      class: ['bs-form', this.getClasses()],
      ...this.getRenderProps(),
      ...domHandlers(this.getListeners({
        submit: (evt: any) => {
          /**
           * The native fallback re-enters this handler, so it has to be fenced.
           *
           * When nobody is listening, a valid form is submitted the plain HTML way --
           * `evt.target.submit()`. Under Vue 3 (and jsdom) that dispatches another `submit`, which
           * arrives back here, validates, submits again, and does not stop: it exhausted the heap
           * rather than failing. Vue 2 never reached the fallback in the same situation.
           */
          if (nativeSubmitInFlight) {
            return
          }

          evt !== void 0 && stopAndPrevent(evt)

          this.validateForm().then(valid => {
            if (valid === true) {
              /**
               * Does anyone up there listen? `$listeners` is gone, and `$attrs` cannot answer either
               * now that `submit` is a declared emit -- Vue removes a declared handler from attrs.
               * What the parent bound is on this component's own vnode. `?.` on `props` only: a
               * mounted component always has its instance and vnode, but `<bs-form>` given no
               * attributes at all renders with `props: null`.
               */
              if ((this as any).$.vnode.props?.onSubmit !== void 0) {
                this.$emit('submit', evt)
              } else if (evt !== void 0 && evt.target !== void 0 && typeof evt.target.submit === 'function') {
                nativeSubmitInFlight = true
                try {
                  evt.target.submit()
                } finally {
                  nativeSubmitInFlight = false
                }
              }
            }
          })
        }
      }))
    }, this.slot(this, 'default'))
  }
})
